/**
 * Unified Auth Provider
 *
 * Provides authentication that works seamlessly for both web (Firebase) and native (Flutter bridge).
 * - Web: Uses Firebase Auth directly (OAuth, email/password)
 * - Native: Calls native auth methods via bridge and syncs with Firebase
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  User as FirebaseUser,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  getIdToken,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { useQueryClient } from "@tanstack/react-query";
import { bridge, BridgeChannels, BridgeActions } from "../bridge";
import { clearSignupTracked } from "../onboardingSave";
import { api, setAuthTokenGetter } from "../api";
import type { LoginEventResponse } from "../api/useLoginEvent";
import {
  getFirebaseAuth,
  signOutFromFirebase,
  signInToFirebaseWithCustomToken,
} from "../firebase";
import { signInFromNativeAuthToken } from "./nativeTokenSignIn";
import {
  consumeRedirectSignIn,
  getAuthRedirectIntent,
  markAuthRedirectPending,
  type AuthRedirectIntent,
} from "./authRedirect";
import { signInNativeShellFromWebSession } from "./nativeShellSignIn";
import { resolveAppPath } from "./useAppNavigation";
import { retryWithBackoff } from "../retry";
import {
  SignInDialogProvider,
  SignInDialogContext,
  type SignInOptions,
} from "./SignInDialogContext";
import { SignInDialog } from "../../components/SignInDialog";
import { useLocation } from "wouter";

// ============================================================================
// Types
// ============================================================================

// User object shape that works for both platforms
export interface AuthUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  imageUrl: string | null;
}

// Auth actions interface (for components that need to call auth methods directly)
export interface AuthActions {
  signInWithGoogle: () => Promise<AuthUser | null>;
  /** Popup-free web path used by the `google_auth_redirect` experiment. Leaves
   *  the page for Google and never returns — callers get nothing back, and pick
   *  the result up on the next load via `consumeRedirectSignIn`. Whatever the
   *  caller would have done in its success handler has to be described in
   *  `intent`, since the handler itself will not exist by then. */
  signInWithGoogleRedirect: (
    intent: Omit<AuthRedirectIntent, "provider">,
  ) => Promise<void>;
  signInWithApple: () => Promise<AuthUser | null>;
  signInWithEmail: (email: string, password: string) => Promise<AuthUser | null>;
  signUpWithEmail: (email: string, password: string, firstName: string, lastName: string) => Promise<AuthUser | null>;
  resetPassword: (email: string) => Promise<void>;
  /** Mails a one-time sign-in code. `"account-not-found"` means the address
   *  has no account and the caller wasn't signing up — the caller shows its own
   *  localized copy for that; `"error"` leaves the message in `error`. */
  requestEmailCode: (
    email: string,
    isSignUp?: boolean,
  ) => Promise<"sent" | "account-not-found" | "rate-limited" | "error">;
  /** Redeems a one-time code for a session. The name is only consulted when
   *  the address has no account yet. Inside the native shell this also signs
   *  the Flutter side in, since the whole flow ran in the WebView. */
  signInWithEmailCode: (
    email: string,
    code: string,
    firstName?: string,
    lastName?: string,
  ) => Promise<AuthUser | null>;
  /** Firebase error code (e.g. "auth/popup-blocked") from the most recent
   *  failed sign-in attempt, or null. Read via ref so it's current even in
   *  stale closures; consumed by auth-funnel failure tracking. */
  getLastAuthErrorCode: () => string | null;
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export interface AuthContextValue {
  /** Whether the auth state has been loaded */
  isLoaded: boolean;

  /** Whether the user is signed in */
  isSignedIn: boolean;

  /** The current user ID */
  userId: string | null;

  /** The current user object (if signed in) */
  user: AuthUser | null;

  /** Sign out the current user */
  signOut: () => Promise<void>;

  /** Get the current auth token */
  getToken: () => Promise<string | null>;

  /** Whether running in native context */
  isNative: boolean;

  /** Show native account dialog (only works on native) */
  showAccountDialog: () => Promise<void>;

  /** Open the sign-in dialog */
  openSignIn: (options?: SignInOptions) => void;

  /** Close the sign-in dialog */
  closeSignIn: () => void;

  /** Whether the sign-in dialog is open */
  isSignInOpen: boolean;
}

// ============================================================================
// Contexts
// ============================================================================

// Persist contexts across hot reloads to prevent "useAuth must be used within AuthProvider" errors
// During HMR, module-level createContext() creates a new object, but consumers may still
// reference the old one. Storing on globalThis ensures the same context object is reused.
const getOrCreateContext = <T,>(key: string, defaultValue: T): React.Context<T> => {
  const globalKey = `__${key}__` as keyof typeof globalThis;
  if (!(globalKey in globalThis)) {
    (globalThis as any)[globalKey] = createContext<T>(defaultValue);
  }
  return (globalThis as any)[globalKey];
};

export const AuthContext = getOrCreateContext<AuthContextValue | null>('AuthContext', null);

// Auth actions context (for direct auth method access, e.g., Migrate page)
export const AuthActionsContext = getOrCreateContext<AuthActions | null>('AuthActionsContext', null);

// Legacy alias for backwards compatibility
export const FirebaseAuthActionsContext = AuthActionsContext;

// ============================================================================
// Helpers
// ============================================================================

// Convert Firebase User to AuthUser
function firebaseUserToAuthUser(user: FirebaseUser): AuthUser {
  const displayNameParts = user.displayName?.split(" ") ?? [];
  return {
    id: user.uid,
    firstName: displayNameParts[0] ?? null,
    lastName: displayNameParts.slice(1).join(" ") || null,
    email: user.email,
    imageUrl: user.photoURL,
  };
}

// ============================================================================
// Inner Auth Provider (needs SignInDialogContext)
// ============================================================================

interface InnerAuthProviderProps {
  children: React.ReactNode;
}

function InnerAuthProvider({ children }: InnerAuthProviderProps) {
  const isNative = bridge.isNative;
  const dialogContext = useContext(SignInDialogContext);

  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prevUserRef = useRef<FirebaseUser | null | undefined>(undefined);
  // Set when the sign-in currently in flight is known to have *created* the
  // account, which the dialog's own `asSignUp` option can't tell us: the email
  // code flow uses one entry point for both, and only the verify response
  // reveals which it was. Consumed (and cleared) by the justSignedIn effect so
  // a fresh account isn't shown the "account-not-found" view.
  const signedUpThisFlowRef = useRef(false);

  const auth = getFirebaseAuth();
  const queryClient = useQueryClient();
  const [,setLocation] = useLocation();

  // The redirect attempt this page load is the far side of, if any. Asked
  // during render, which is what latches it for everything that asks after
  // `consumeRedirectSignIn` has claimed the marker.
  const redirectIntent = getAuthRedirectIntent();
  const cameFromRedirect = redirectIntent !== null;

  // Listen for auth state changes from Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Detect if user just signed in (was null/undefined, now has user)
      const wasNotSignedIn = prevUserRef.current === null;
      const isNowSignedIn = firebaseUser !== null;
      const isInitialCheck = prevUserRef.current === undefined;
      // A redirect sign-in arrives as the *initial* auth state: the SDK has
      // already persisted the session by the time the first callback runs, so
      // the null→user transition every other flow produces never happens. The
      // marker is the only thing that separates "just signed in over there"
      // from "arrived with a session"; without it the login event, and every
      // side effect hanging off it, is skipped for the whole redirect arm.
      const justSignedIn = isInitialCheck
        ? redirectIntent !== null && isNowSignedIn
        : wasNotSignedIn && isNowSignedIn;
      // Sign-out transition: was a real user, now null. Drop all user-scoped
      // cache exactly once here — not on every signed-out render — so stale
      // private data can't leak across sessions without nuking public-page
      // caches on each mount.
      const justSignedOut = prevUserRef.current != null && firebaseUser === null;

      // Update refs and state
      prevUserRef.current = firebaseUser;
      setUser(firebaseUser);
      setIsLoaded(true);

      if (justSignedOut) {
        queryClient.clear();
        // Whoever signs up next in this browser is a different person, and
        // their signup has to be reportable. See `trackSignupOnce`.
        clearSignupTracked();
      }

      if (justSignedIn && dialogContext && firebaseUser) {
        try {
          // Block dialog dismissal on login event; use token from current user so request is authenticated before state updates
          const tokenGetter = () => getIdToken(firebaseUser);
          const platform = bridge.isNative ? (bridge.platform || "native") : "web";
          const result = (await api.post<LoginEventResponse>(
            "/user/login-event",
            { platform },
            tokenGetter
          )) as LoginEventResponse;
          const quizCompleted = result?.quizCompleted === true;
          const justSignedUp = signedUpThisFlowRef.current;
          signedUpThisFlowRef.current = false;

          // Everything below steers the dialog, so it only applies when the
          // dialog is what signed the user in. The onboarding flow embeds its
          // own SignInForm and advances itself on success; running the
          // dialog's completion for that sign-in fired `onSuccess` from
          // whichever caller opened the dialog last and redirected the user
          // out of the flow. The login event above stays unconditional —
          // every sign-in should be recorded, whatever surface it came from.
          //
          // A redirect sign-in that started in the dialog comes back to a page
          // where the dialog is closed and its options are gone, so the intent
          // recorded before leaving stands in for both. `surface` is what keeps
          // an inline redirect out of here, exactly as `isOpen` does for popups.
          const fromDialogRedirect = redirectIntent?.surface === "dialog";
          if (!dialogContext.isOpen && !fromDialogRedirect) return;

          const asSignUp = fromDialogRedirect
            ? redirectIntent?.asSignUp
            : dialogContext.options.asSignUp;
          const wasLoginNotSignup = asSignUp !== true && !justSignedUp;
          // A code sign-in on a sign-in-flavored surface can still create the
          // account, and nothing collected a name along the way. Closing here
          // would strand the new profile as "User", so hold the dialog open on
          // the name step instead.
          const needsNameStep = justSignedUp && !firebaseUser.displayName;

          if (wasLoginNotSignup && !quizCompleted) {
            // Reopen rather than just set the view when the dialog isn't there:
            // this branch exists to offer a choice ("create a profile" vs "try
            // another account"), and letting ProtectedRoute quietly forward to
            // the quiz instead would make that choice for the user.
            if (!dialogContext.isOpen) dialogContext.open({ asSignUp: false });
            dialogContext.setView("account-not-found");
          } else if (needsNameStep) {
            if (!dialogContext.isOpen) dialogContext.open({ asSignUp: true });
            dialogContext.setView("email-code-name");
          } else if (fromDialogRedirect) {
            // The caller's `onSuccess` died with the page it was closed over,
            // so the destination it recorded is all there is to go on. No
            // destination means the right answer was to stay put, which this
            // page load already did.
            if (redirectIntent?.returnTo) setLocation(resolveAppPath(redirectIntent.returnTo));
          } else {
            // Signup flow: still close and let app redirect to quiz
            dialogContext.options.onSuccess?.();
            dialogContext.forceClose();
          }
        } catch (e) {
          console.error("[AuthProvider] Login event failed", e);
          // On error, close dialog and call onSuccess so user isn’t stuck —
          // but only if the dialog is the surface holding them up.
          if (redirectIntent?.surface === "dialog") {
            if (redirectIntent.returnTo) setLocation(resolveAppPath(redirectIntent.returnTo));
            return;
          }
          if (!dialogContext.isOpen) return;
          dialogContext.options.onSuccess?.();
          dialogContext.forceClose();
        }
      }
    });

    return unsubscribe;
  }, [auth, dialogContext, queryClient, redirectIntent, setLocation]);

  // Settle the redirect this load came back from, whether or not a sign-in form
  // is mounted to care about it. SignInForm awaits the same memoized promise
  // for its surface's success/failure handling; this call is what guarantees
  // the marker is claimed even when the user was redirected from a surface that
  // no longer exists — an unclaimed one would make the next load believe it too
  // had just come back from Google.
  useEffect(() => {
    if (!cameFromRedirect) return;
    void consumeRedirectSignIn(auth).then((outcome) => {
      if (!outcome || outcome.user) return;
      // The code is the whole diagnosis on this path and the user-facing copy
      // deliberately doesn't carry it: `auth/missing-initial-state` means the
      // handler couldn't reach its storage (a cross-origin authDomain), while
      // `no-user` means the round trip simply came back empty. Without this
      // the two are indistinguishable from the outside.
      console.error(
        "[AuthProvider] Redirect sign-in returned no user:",
        outcome.errorCode,
      );
      setError("Failed to sign in with Google. Please try again.");
    });
  }, [auth, cameFromRedirect]);

  // Listen for auth credentials from native bridge (custom token sign-in)
  useEffect(() => {
    if (!isNative) return;

    const unsubscribe = bridge.on(
      BridgeChannels.AUTH,
      BridgeActions.STATE_CHANGED,
      async (payload: {
        userId: string | null;
        token: string | null;
        user: AuthUser | null;
      }) => {
        // Skip re-auth if already signed in as the same user — the native bridge
        // fires STATE_CHANGED on every tap, and calling signInWithCustomToken when
        // already authenticated causes a brief null→user auth state cycle that
        // unmounts/remounts components (visible as particle flicker, etc.).
        const currentUid = getFirebaseAuth().currentUser?.uid ?? null;
        if (payload.userId && currentUid === payload.userId) return;

        console.log("[AuthProvider] Native auth state changed:", JSON.stringify(payload, null, 2));
        if (payload.token) {
          const signedIn = await signInFromNativeAuthToken(payload.token);
          if (!signedIn) {
            setError(
              "Couldn't sign you in. Please check your connection and try again."
            );
          }
        } else {
          await signOutFromFirebase();
        }
      }
    );

    return unsubscribe;
  }, [isNative]);

  // Initial native auth sync - get token from native and sign in to Firebase
  useEffect(() => {
    if (!isNative) return;
    async function syncNativeAuth() {
      try {
        const token = await bridge.request<string | null>(
          BridgeChannels.AUTH,
          BridgeActions.GET_TOKEN
        );
        if (token) {
          await signInFromNativeAuthToken(token);
        }
      } catch (e) {
        console.error("[AuthProvider] Failed to sync native auth:", e);
      }
    }
    syncNativeAuth();
  }, [isNative]);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      return await getIdToken(user);
    } catch (e) {
      console.error("[AuthProvider] getToken error:", e);
      return null;
    }
  }, [user]);
  setAuthTokenGetter(getToken);

  const handleSignOut = useCallback(async () => {
    try {
      if (isNative) {
        await bridge.request(BridgeChannels.AUTH, BridgeActions.SIGN_OUT);
      }
      await firebaseSignOut(auth);
      setLocation("/");
    } catch (e) {
      console.error("[AuthProvider] Sign out error:", e);
    }
  }, [auth, isNative, setLocation]);

  // Native auth via bridge - supports 'google', 'apple', 'email' methods
  // Returns AuthUser on success, null on failure/cancellation
  const nativeAuth = useCallback(
    async (
      method: "google" | "apple" | "email",
      credentials?: { email: string; password: string }
    ): Promise<AuthUser | null> => {
      if (!isNative) {
        setError("Not running in native context");
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await bridge.request<{
          success: boolean;
          userId?: string;
          token?: string;
          error?: string;
        }>(
          BridgeChannels.AUTH,
          BridgeActions.NATIVE_AUTH,
          { method, credentials },
          60000 * 2 // 2 minute timeout for auth
        );

        if (result.success && result.token) {
          // Sign in to Firebase with the token from native
          const firebaseUser = await signInFromNativeAuthToken(result.token);
          if (firebaseUser) {
            return firebaseUserToAuthUser(firebaseUser);
          }
          setError(
            "Couldn't finish signing you in. Please check your connection and try again."
          );
          return null;
        } else {
          const errorMsg = result.error || "Authentication failed";
          if (errorMsg !== "cancelled") {
            setError(errorMsg);
          }
          return null;
        }
      } catch (e: any) {
        const errorMsg = e.message || "Authentication failed";
        setError(errorMsg);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isNative]
  );

  // Firebase error code of the most recent failed sign-in, kept in a ref so
  // callers (which hold the actions object from an earlier render) always read
  // the current value. Cleared at the start of every attempt.
  const lastAuthErrorCodeRef = useRef<string | null>(null);
  const getLastAuthErrorCode = useCallback(
    () => lastAuthErrorCodeRef.current,
    []
  );

  // Auth methods - work for both web and native
  // Returns AuthUser on success, null on failure/cancellation
  const signInWithGoogle = useCallback(async (): Promise<AuthUser | null> => {
    if (isNative) {
      // Use native bridge for Google SSO
      return await nativeAuth("google");
    }
    setError(null);
    lastAuthErrorCodeRef.current = null;
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("profile");
      provider.addScope("email");
      const result = await signInWithPopup(auth, provider);
      return result.user ? firebaseUserToAuthUser(result.user) : null;
    } catch (e: any) {
      lastAuthErrorCodeRef.current = e.code || "unknown";
      if (e.code === "auth/network-request-failed") {
        setError("Network error. Please check your connection and try again.");
      } else if (
        e.code !== "auth/popup-closed-by-user" &&
        e.code !== "auth/cancelled-popup-request"
      ) {
        console.error("[AuthProvider] Google sign-in error:", e);
        setError("Failed to sign in with Google. Please try again.");
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [auth, isNative, nativeAuth]);

  // The `google_auth_redirect` experiment's popup-free web path. On success
  // this call never returns — the tab is Google's from here, and the session
  // is picked up by `consumeRedirectSignIn` on the load that comes back. The
  // marker is written first so that load can tell a completed sign-in from a
  // user who simply arrives with a session already in hand.
  const signInWithGoogleRedirect = useCallback(async (
    intent: Omit<AuthRedirectIntent, "provider">,
  ): Promise<void> => {
    setError(null);
    lastAuthErrorCodeRef.current = null;
    markAuthRedirectPending({ provider: "google", ...intent });
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("profile");
      provider.addScope("email");
      await signInWithRedirect(auth, provider);
    } catch (e: any) {
      // Only reachable if the navigation itself was refused, so the page is
      // still here and owes the user an explanation.
      lastAuthErrorCodeRef.current = e.code || "unknown";
      console.error("[AuthProvider] Google redirect sign-in error:", e);
      setError("Failed to sign in with Google. Please try again.");
      setIsLoading(false);
    }
  }, [auth]);

  // Returns AuthUser on success, null on failure/cancellation
  const signInWithApple = useCallback(async (): Promise<AuthUser | null> => {
    if (isNative) {
      // Use native bridge for Apple SSO
      return await nativeAuth("apple");
    }

    setError(null);
    lastAuthErrorCodeRef.current = null;
    try {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      const result = await signInWithPopup(auth, provider);
      return result.user ? firebaseUserToAuthUser(result.user) : null;
    } catch (e: any) {
      lastAuthErrorCodeRef.current = e.code || "unknown";
      if (e.code === "auth/network-request-failed") {
        setError("Network error. Please check your connection and try again.");
      } else if (
        e.code !== "auth/popup-closed-by-user" &&
        e.code !== "auth/cancelled-popup-request"
      ) {
        console.error("[AuthProvider] Apple sign-in error:", e);
        setError("Failed to sign in with Apple. Please try again.");
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [auth, isNative, nativeAuth]);

  // Returns AuthUser on success, null on failure
  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<AuthUser | null> => {
      if (isNative) {
        // Use native bridge for email sign-in (credentials stored on native)
        return await nativeAuth("email", { email, password });
      }

      setIsLoading(true);
      setError(null);
      try {
        const result = await signInWithEmailAndPassword(auth, email, password);
        return result.user ? firebaseUserToAuthUser(result.user) : null;
      } catch (e: any) {
        console.error("[AuthProvider] Email sign-in error:", e);
        if (e.code === "auth/user-not-found") {
          setError("No account found with this email. Please sign up.");
        } else if (
          e.code === "auth/wrong-password" ||
          e.code === "auth/invalid-credential"
        ) {
          setError("Incorrect password. Please try again.");
        } else if (e.code === "auth/invalid-email") {
          setError("Invalid email address.");
        } else if (e.code === "auth/too-many-requests") {
          setError("Too many failed attempts. Please try again later.");
        } else if (e.code === "auth/network-request-failed") {
          setError("Network error. Please check your connection and try again.");
        } else {
          setError("Failed to sign in. Please try again.");
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth, isNative, nativeAuth]
  );

  // Sign up always uses Firebase directly (both web and native)
  // This way user data is stored in Firestore regardless of platform
  // Returns AuthUser on success, null on failure
  const signUpWithEmail = useCallback(
    async (
      email: string,
      password: string,
      firstName: string,
      lastName: string
    ): Promise<AuthUser | null> => {
      setIsLoading(true);
      setError(null);
      try {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        // Update the user's display name
        const displayName = [firstName, lastName].filter(Boolean).join(" ");
        if (displayName) {
          await updateProfile(userCredential.user, { displayName });
        }
        // Non-blocking: EmailVerificationBanner offers a manual resend if all
        // attempts fail, so a lost verification email never blocks sign-up.
        retryWithBackoff(() => sendEmailVerification(userCredential.user), {
          attempts: 3,
          baseDelayMs: 2000,
        }).catch((e) => {
          console.error("[AuthProvider] sendEmailVerification failed:", e);
        });
        return signInWithEmail(email, password);
      } catch (e: any) {
        console.error("[AuthProvider] Email sign-up error:", e);
        if (e.code === "auth/email-already-in-use") {
          setError(
            "An account with this email already exists. Please sign in."
          );
        } else if (e.code === "auth/weak-password") {
          setError("Password is too weak. Please use at least 6 characters.");
        } else if (e.code === "auth/invalid-email") {
          setError("Invalid email address.");
        } else if (e.code === "auth/network-request-failed") {
          setError("Network error. Please check your connection and try again.");
        } else {
          setError("Failed to create account. Please try again.");
        }
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [auth, signInWithEmail]
  );

  // ---- One-time email code -------------------------------------------------
  // Unlike every other method here, this path never touches the Firebase Web
  // SDK's own credential APIs. The server verifies the code, resolves (or
  // creates) the Firebase user, and returns a custom token we exchange — which
  // is why it works the same on web and inside the native WebView.
  const requestEmailCode = useCallback(
    async (
      email: string,
      isSignUp = false,
    ): Promise<"sent" | "account-not-found" | "rate-limited" | "error"> => {
      setIsLoading(true);
      setError(null);
      try {
        await api.post<{ expiresInSeconds: number; resendAfterSeconds: number }>(
          "/auth/email-code/request",
          { email, isSignUp },
        );
        return "sent";
      } catch (e: any) {
        console.error("[AuthProvider] Email code request error:", e);
        // 404 and 429 get their own branches so the caller can show localized
        // copy next to the field rather than the server's English message.
        if (e?.status === 404) return "account-not-found";
        if (e?.status === 429) return "rate-limited";
        // Send failures carry user-facing copy from the server.
        setError(e?.message || "We couldn't send your code. Please try again.");
        return "error";
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const signInWithEmailCode = useCallback(
    async (
      email: string,
      code: string,
      firstName?: string,
      lastName?: string,
    ): Promise<AuthUser | null> => {
      setIsLoading(true);
      setError(null);
      lastAuthErrorCodeRef.current = null;
      try {
        const response = await api.post<{
          customToken: string;
          isNewUser: boolean;
        }>("/auth/email-code/verify", {
          email,
          code,
          ...(firstName ? { firstName } : {}),
          ...(lastName ? { lastName } : {}),
        });

        if (!response.success || !response.data?.customToken) {
          setError(response.error || "We couldn't sign you in. Please try again.");
          return null;
        }

        // Must be set before the sign-in lands, since onAuthStateChanged fires
        // synchronously off it and reads this ref.
        signedUpThisFlowRef.current = response.data.isNewUser === true;

        const firebaseUser = await signInToFirebaseWithCustomToken(
          response.data.customToken,
        );
        if (!firebaseUser) {
          setError("We couldn't sign you in. Please try again.");
          return null;
        }

        // In the app the shell owns push registration, IAP and the account
        // sheet, and it has no session of its own after a WebView-only
        // sign-in. Fire-and-forget: a failed handoff degrades native features
        // but must not fail a sign-in that already succeeded.
        if (isNative) {
          void signInNativeShellFromWebSession(() => firebaseUser.getIdToken());
        }

        return firebaseUserToAuthUser(firebaseUser);
      } catch (e: any) {
        console.error("[AuthProvider] Email code sign-in error:", e);
        // The server's verify `reason` (invalid_code, expired, ...) when it sent one.
        lastAuthErrorCodeRef.current = e?.reason || "verify_failed";
        setError(e?.message || "We couldn't sign you in. Please try again.");
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [isNative],
  );

  // Password reset works the same for both web and native
  const resetPasswordEmail = useCallback(
    async (email: string) => {
      setIsLoading(true);
      setError(null);
      try {
        await sendPasswordResetEmail(auth, email);
      } catch (e: any) {
        console.error("[AuthProvider] Password reset error:", e);
        if (e.code === "auth/user-not-found") {
          setError("No account found with this email address.");
        } else if (e.code === "auth/invalid-email") {
          setError("Invalid email address.");
        } else if (e.code === "auth/network-request-failed") {
          setError("Network error. Please check your connection and try again.");
        } else {
          setError("Failed to send reset email. Please try again.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [auth]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const showAccountDialog = useCallback(async () => {
    if (!isNative) return;

    try {
      const result = await bridge.request<{ action: string }>(
        BridgeChannels.AUTH,
        BridgeActions.SHOW_ACCOUNT_DIALOG,
        undefined,
        60000 * 5
      );
      if (result.action === "signOut") {
        await signOutFromFirebase();
      }
    } catch (e) {
      console.error("[AuthProvider] Show account dialog error:", e);
    }
  }, [isNative]);

  // Dialog control - delegate to SignInDialogContext
  const openSignIn = useCallback(
    (options?: SignInOptions) => {
      dialogContext?.open(options);
    },
    [dialogContext]
  );

  const closeSignIn = useCallback(() => {
    dialogContext?.close();
  }, [dialogContext]);

  // Auth context value
  const authValue = useMemo<AuthContextValue>(
    () => ({
      isLoaded,
      isSignedIn: !!user,
      userId: user?.uid ?? null,
      user: user ? firebaseUserToAuthUser(user) : null,
      signOut: handleSignOut,
      getToken,
      isNative,
      showAccountDialog,
      openSignIn,
      closeSignIn,
      isSignInOpen: dialogContext?.isOpen ?? false,
    }),
    [
      isLoaded,
      user,
      handleSignOut,
      getToken,
      isNative,
      showAccountDialog,
      openSignIn,
      closeSignIn,
      dialogContext?.isOpen,
    ]
  );

  // Auth actions context value (for direct auth method access)
  const actionsValue = useMemo<AuthActions>(
    () => ({
      signInWithGoogle,
      signInWithGoogleRedirect,
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      resetPassword: resetPasswordEmail,
      requestEmailCode,
      signInWithEmailCode,
      getLastAuthErrorCode,
      isLoading,
      error,
      clearError,
    }),
    [
      signInWithGoogle,
      signInWithGoogleRedirect,
      signInWithApple,
      signInWithEmail,
      signUpWithEmail,
      resetPasswordEmail,
      requestEmailCode,
      signInWithEmailCode,
      getLastAuthErrorCode,
      isLoading,
      error,
      clearError,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>
      <AuthActionsContext.Provider value={actionsValue}>
        {children}
        {/* Sign-in dialog - rendered here, state in SignInDialogContext */}
        <SignInDialog />
      </AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}

// ============================================================================
// Auth Provider (wraps with SignInDialogProvider)
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <SignInDialogProvider>
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </SignInDialogProvider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

/**
 * Convenience hook to check if user is signed in
 */
export function useIsSignedIn(): boolean {
  return useAuth().isSignedIn;
}

/**
 * Convenience hook to get current user ID
 */
export function useUserId(): string | null {
  return useAuth().userId;
}

/**
 * Convenience hook to get current user
 */
export function useUser(): AuthUser | null {
  return useAuth().user;
}

/**
 * Hook to open sign-in dialog (convenience wrapper)
 */
export function useSignIn() {
  const { openSignIn, closeSignIn, isSignInOpen } = useAuth();
  return { openSignIn, closeSignIn, isSignInOpen };
}

/**
 * Hook to access auth actions directly (for special cases like Migrate page)
 */
export function useAuthActions(): AuthActions {
  const context = useContext(AuthActionsContext);
  if (!context) {
    throw new Error("useAuthActions must be used within an AuthProvider");
  }
  return context;
}
